import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Joyride } from 'react-joyride';

import i18n from '@core/i18n';
import { useAppSelector } from '@core/store/hooks';
import { selectCurrentUser, selectIsGuest } from '@features/auth';
import { selectWorkspaceTourRequestId } from '@features/workspace';

const TOUR_VERSION = 'v1';

// Titles and contents are i18n keys, resolved by `buildTourSteps`.
const TOUR_STEPS = [
  {
    id: 'welcome',
    target: 'body',
    placement: 'center',
    title: 'workspace.tour.welcome.title',
    content: 'workspace.tour.welcome.content',
  },
  {
    id: 'project-selector',
    target: '[data-tour="project-selector"]',
    placement: 'right-start',
    title: 'workspace.tour.projectSelector.title',
    content: 'workspace.tour.projectSelector.content',
  },
  {
    id: 'new-chat',
    target: '[data-tour="new-chat"]',
    placement: 'right',
    title: 'workspace.tour.newChat.title',
    content: 'workspace.tour.newChat.content',
  },
  {
    id: 'dataroom',
    target: '[data-tour="project-files"]',
    placement: 'right-start',
    title: 'workspace.tour.dataroom.title',
    content: 'workspace.tour.dataroom.content',
  },
  {
    id: 'conversations',
    target: '[data-tour="project-conversations"]',
    placement: 'right',
    title: 'workspace.tour.conversations.title',
    content: 'workspace.tour.conversations.content',
  },
  {
    id: 'chat',
    target: '[data-tour="workspace-chat"]',
    placement: 'center',
    title: 'workspace.tour.chat.title',
    content: 'workspace.tour.chat.content',
  },
  {
    id: 'memory',
    target: '[data-tour="chat-composer"]',
    placement: 'top',
    title: 'workspace.tour.memory.title',
    content: 'workspace.tour.memory.content',
  },
  {
    id: 'ready',
    target: '[data-tour="chat-composer"]',
    placement: 'top',
    title: 'workspace.tour.ready.title',
    content: 'workspace.tour.ready.content',
  },
];

// Guests are view-only: no project creation, no uploads, no Settings. Their
// tour drops the Knowledge Base step and rewords the steps that would
// otherwise promise those write actions.
const GUEST_EXCLUDED_STEPS = new Set(['memory']);

const GUEST_STEP_OVERRIDES = {
  'project-selector': {
    title: 'workspace.tour.guestProjectSelector.title',
    content: 'workspace.tour.guestProjectSelector.content',
  },
  dataroom: {
    title: 'workspace.tour.guestDataroom.title',
    content: 'workspace.tour.guestDataroom.content',
  },
  chat: {
    title: 'workspace.tour.guestChat.title',
    content: 'workspace.tour.guestChat.content',
  },
};

/**
 * Pure builder so tests can assert both variants without mounting Joyride.
 * Strips the internal id: Joyride only receives its own step fields, with
 * the copy resolved in the active language.
 */
export function buildTourSteps(isGuest, t = i18n.t.bind(i18n)) {
  const steps = isGuest
    ? TOUR_STEPS
        .filter((step) => !GUEST_EXCLUDED_STEPS.has(step.id))
        .map((step) => ({ ...step, ...GUEST_STEP_OVERRIDES[step.id] }))
    : TOUR_STEPS;
  return steps.map((step) => {
    const joyrideStep = { ...step, title: t(step.title), content: t(step.content) };
    delete joyrideStep.id;
    return joyrideStep;
  });
}

function getTourStorageKey(user) {
  return `neovision:workspace-tour:${TOUR_VERSION}:${user?.uuid || user?.email || 'user'}`;
}

export function WorkspaceTour({ active }) {
  const { t } = useTranslation();
  const user = useAppSelector(selectCurrentUser);
  const isGuest = useAppSelector(selectIsGuest);
  const requestId = useAppSelector(selectWorkspaceTourRequestId);
  const storageKey = useMemo(() => getTourStorageKey(user), [user]);
  const steps = useMemo(() => buildTourSteps(isGuest, t), [isGuest, t]);
  const [run, setRun] = useState(false);

  useEffect(() => {
    if (!active) {
      setRun(false);
      return;
    }

    let hasCompletedTour = false;
    try {
      hasCompletedTour = localStorage.getItem(storageKey) === 'complete';
    } catch {
      // A blocked storage API should not prevent the tour from running.
    }
    setRun(!hasCompletedTour);
  }, [active, storageKey]);

  useEffect(() => {
    if (!active || requestId === 0) return;
    setRun(false);
    const frame = requestAnimationFrame(() => setRun(true));
    return () => cancelAnimationFrame(frame);
  }, [active, requestId]);

  const handleEvent = ({ type, status }) => {
    if (type !== 'tour:end') return;
    setRun(false);
    if (status === 'finished' || status === 'skipped') {
      try {
        localStorage.setItem(storageKey, 'complete');
      } catch {
        // Completion persistence is optional.
      }
    }
  };

  return (
    <Joyride
      continuous
      run={run}
      steps={steps}
      scrollToFirstStep
      onEvent={handleEvent}
      locale={{
        back: t('workspace.tour.back'),
        close: t('workspace.tour.close'),
        last: t('workspace.tour.last'),
        next: t('workspace.tour.next'),
        // Joyride's own single-brace placeholders, left for it to fill.
        nextWithProgress: t('workspace.tour.nextWithProgress'),
        skip: t('workspace.tour.skip'),
      }}
      options={{
        buttons: ['back', 'skip', 'primary'],
        closeButtonAction: 'skip',
        overlayClickAction: false,
        dismissKeyAction: false,
        showProgress: true,
        skipBeacon: true,
        spotlightPadding: 8,
        spotlightRadius: 10,
        overlayColor: 'rgba(2, 20, 39, 0.66)',
        primaryColor: '#07559D',
        textColor: '#172033',
        width: 360,
        zIndex: 100,
      }}
      styles={{
        tooltip: { borderRadius: 14, padding: 20 },
        tooltipContainer: { textAlign: 'left' },
        tooltipTitle: { fontSize: 18, lineHeight: 1.35 },
        tooltipContent: { color: '#526075', fontSize: 14, lineHeight: 1.6, paddingBottom: 18, paddingTop: 10 },
        buttonPrimary: { borderRadius: 8, fontSize: 14, fontWeight: 600, padding: '10px 14px' },
        buttonBack: { fontSize: 14 },
        buttonSkip: { fontSize: 13 },
      }}
    />
  );
}
